import { openToastr } from '@Common/handlers/services';
import { useSafeDispatch } from '@Common/hooks';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useEffect, useState } from 'react';
import { initialize } from '../components/EditForm/actionCreators';
import { useEditFormDispatch } from '../components/EditForm/context';
import { useCitationGeneratorDispatch, useCitationGeneratorState } from '../context';
import {
  addCitationToSearchResults,
  incrementPageNo,
  setBibSearchQuery,
  setIsLastPage,
  setLoadingResults,
  startSearchMode,
  stopSearchMode,
  toggleCitationGeneratorEditModal,
} from '../context/actionCreators';
import { bibSearchApi, crossrefSearchApi } from '../handlers/api';
import citationGeneratorTrack from '../handlers/track';
import { CitationSourceEnum } from '../types';
import { crossrefRespToCitation } from './crossref';
import { ApiResponseForBiblioSearch, bibToCitation } from './oclc';

const MIN_QUERY_LENGTH = 4;
const LS_SORT_BY_KEY = 'cg-journal-search-sortby';

export const useSourceSearchApis = ({ onSearch }: { onSearch?: Function } = {}) => {
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();

  const searchType = useCitationGeneratorState((state) => state.searchType);
  const pageNo = useCitationGeneratorState((state) => state.pageNo);
  const isLoadingResults = useCitationGeneratorState((state) => state.isLoadingResults);
  const bibSearchQuery = useCitationGeneratorState((state) => state.bibSearchQuery);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [query, _setQuery] = useState('');
  const setQuery = useSafeDispatch(_setQuery);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [sortBy, _setSortBy] = useState(
    QuillLocalStorage.getInstance().get(LS_SORT_BY_KEY, 'relevance')
  );
  const setSortBy = useSafeDispatch(_setSortBy);

  const searchSource = (pageNumber = 1, forceCalled?: boolean) => {
    if (isLoadingResults || (!forceCalled && query === bibSearchQuery)) {
      return;
    }

    if (pageNumber === 1) {
      citationGeneratorDispatch(incrementPageNo(true));
    }

    const sanitizedQuery = query.trim();
    if (sanitizedQuery !== query) {
      setQuery(sanitizedQuery);
    }

    citationGeneratorDispatch(setLoadingResults(true));
    citationGeneratorDispatch(setIsLastPage(false));

    if (searchType === CitationSourceEnum.JOURNAL) {
      // crossref search
      crossrefSearchApi({ query: sanitizedQuery, page: pageNumber, sortBy })
        .then((response) => {
          if (!response.items?.length || !response['total-results']) {
            openToastr('No results found');
            citationGeneratorDispatch(setBibSearchQuery(''));
            citationGeneratorDispatch(addCitationToSearchResults([]));
          }

          const citations = crossrefRespToCitation(response);
          citationGeneratorDispatch(setBibSearchQuery(sanitizedQuery));
          citationGeneratorDispatch(setIsLastPage(response['items-per-page'] < 10));
          citationGeneratorDispatch(addCitationToSearchResults(citations));
        })
        .catch((e) => {
          openToastr(e || 'Something went wrong with Journal search');
          citationGeneratorDispatch(stopSearchMode());
          citationGeneratorDispatch(setIsLastPage(true));
          citationGeneratorDispatch(addCitationToSearchResults([]));
          citationGeneratorDispatch(setBibSearchQuery(''));
        })
        .finally(() => {
          citationGeneratorDispatch(setLoadingResults(false));
        });
    } else {
      // bib search
      bibSearchApi({
        query: sanitizedQuery,
        page: pageNumber,
        publicationType: searchType,
      })
        .then((d: ApiResponseForBiblioSearch) => {
          if (!d.records?.length) {
            openToastr('No results found');
            citationGeneratorDispatch(setBibSearchQuery(''));
            citationGeneratorDispatch(addCitationToSearchResults([]));
            return;
          }

          if (searchType === CitationSourceEnum.WEBSITE) {
            const citation = bibToCitation(d.records[0], searchType, sanitizedQuery);
            const formValues = {
              ...citation,
              citationSource: searchType,
            };
            citationGeneratorDispatch(toggleCitationGeneratorEditModal(true, formValues));
            editFormDispatch(initialize(formValues));
          } else {
            const citations = d.records.map((bib) => bibToCitation(bib, searchType));
            citationGeneratorDispatch(setBibSearchQuery(sanitizedQuery));
            citationGeneratorDispatch(setIsLastPage(d.records?.length < 10));
            citationGeneratorDispatch(addCitationToSearchResults(citations));
          }
        })
        .catch((e) => {
          if (searchType === CitationSourceEnum.WEBSITE && e?.status === 400) {
            openToastr(e?.data?.text);
          } else {
            openToastr('Something went wrong!');
          }
          citationGeneratorDispatch(stopSearchMode());
          citationGeneratorDispatch(setIsLastPage(true));
          citationGeneratorDispatch(incrementPageNo(true));
          citationGeneratorDispatch(addCitationToSearchResults([]));
          citationGeneratorDispatch(setBibSearchQuery(''));
        })
        .finally(() => {
          citationGeneratorDispatch(setLoadingResults(false));
        });
    }
  };

  useEffect(() => {
    if (bibSearchQuery) {
      searchSource(pageNo, true);
    }
  }, [pageNo]);

  useEffect(() => {
    QuillLocalStorage.getInstance().set(LS_SORT_BY_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    if (query.trim()?.length >= MIN_QUERY_LENGTH) {
      citationGeneratorDispatch(startSearchMode());
      citationGeneratorDispatch(incrementPageNo(true));
      citationGeneratorDispatch(addCitationToSearchResults([]));
      searchSource(1, true);
      onSearch?.();
    }
  }, [searchType, sortBy]);

  const search = () => {
    if (query?.trim().length >= MIN_QUERY_LENGTH) {
      citationGeneratorDispatch(startSearchMode());
      if (searchType === CitationSourceEnum.WEBSITE) {
        searchSource(1, true);
        citationGeneratorTrack.userSearchedQuery(searchType, query);
      } else if (query !== bibSearchQuery) {
        searchSource();
        citationGeneratorTrack.userSearchedQuery(searchType, query);
      }
      onSearch?.();
    } else {
      openToastr(`Please enter minimum ${MIN_QUERY_LENGTH} characters to search`);
    }
  };

  return { query, setQuery, search, sortBy, setSortBy };
};
